import { Injectable, signal, WritableSignal } from '@angular/core';
import { OverviewReportResponse, PieChartData, SummaryPage } from '../components/report/report.model';
import { FilterRequest } from '../shared/types';
import {
  Customer_Sentiment,
  Insights,
  Interaction_Summary,
  Interaction_Summary_List,
  Interaction_Time,
} from '../components/report/report.constants';

export interface OverviewReportRequestData {
  filters: FilterRequest[];
  start_date: string;
  end_date: string;
  timeZone: string;
}
@Injectable({
  providedIn: 'root',
})
export class CustomerReportService {
  private readonly HIDE_INFO_MSG = 'HIDE_INFO_MSG';
  private _hideInfoMsg: WritableSignal<boolean>;

  constructor() {
    const hideInfoMsg = localStorage.getItem(this.HIDE_INFO_MSG) ?? false;
    this._hideInfoMsg = signal<boolean>(hideInfoMsg === 'true');
  }

  public get hideInfoMsg(): WritableSignal<boolean> {
    return this._hideInfoMsg;
  }

  setHideInfoMsg(status: boolean) {
    localStorage.setItem(this.HIDE_INFO_MSG, `${status}`);
    this._hideInfoMsg.set(status);
  }

  createInteractionPages(data: OverviewReportResponse) {
    return data.conversations.conversations.map((conversation) => {
      return {
        ConversationId: conversation.ConversationId,
        ...this.mapKeys(Interaction_Summary, conversation),
        ...this.mapKeys(Interaction_Summary_List, conversation),
        ...this.mapKeys(Interaction_Time, conversation),
        ...this.mapKeys(Customer_Sentiment, conversation),
        ...this.mapKeys(Insights, conversation, 'value'),
      };
    });
  }

  private mapKeys(mappingArray: any[], conversation: any, keyType: string = 'key'): any {
    return mappingArray.reduce((acc, { label, key, value, uiKey }) => {
      const path = value || key;
      acc[uiKey] = this.resolvePath(conversation, path);
      return acc;
    }, {});
  }

  private resolvePath(object: any, path: string): any {
    return path.split('.').reduce((o, p) => (o ? o[p] : undefined), object);
  }

  createSummaryPage(data: OverviewReportResponse): SummaryPage {
    return {
      dateRange: data.dateRange,
      aeroplaneNumber: data.aeroplaneNumber,
      phoneNumber: data.phoneNumber.join(', '),
      totalConversations: data.totalConversations.toString(),
      totalPrimaryTopics: data.totalPrimaryTopics.toString(),
      totalTopics: data.totalTopics.toString(),
      totalRootCauses: data.totalRootCauses.toString(),
      listOfPrimaryTopics: data.listOfPrimaryTopics.join(', '),
      listofRootCauses: data.listofRootCauses.join(', '),
      averageInteractionTime: data.averageInteractionTime,
      averageQueueWaitTime: data.averageQueueWaitTime,
      averageHoldTime: data.averageHoldTime,
      channelDistribution: JSON.stringify(data.channelDistribution),
      resolutionDistribution: JSON.stringify(data.resolutionDistribution),
    };
  }

  transformDataToPieChartData(labels: string[], percentages: { [key: string]: number }): PieChartData[] {
    const colors = ['#B6C8A9', '#FF9B71', '#F2D0A4', '#CABAC8', '#BBDEF0'];

    return labels.map((label, index) => {
      const data: PieChartData = {
        name: label,
        y: percentages[label],
      };

      if (colors[index % colors.length]) {
        data.color = colors[index % colors.length];
      }

      return data;
    });
  }
}
